import {
  createMuiTheme,
  TextField,
  ThemeProvider,
  useMediaQuery,
  withStyles,
} from '@material-ui/core'
import * as React from 'react'
import styled from 'styled-components'
import { useFirebaseAuthGuard } from '../../../hooks/firebase'
import { animated, useSpring } from 'react-spring'
import { VideoData } from '../../../interfaces'
import { spacebarListenerActiveAtom } from '../videoplayer/atoms'
import ProfileImage from '../../profile/image'
import { FbEnrichedUser } from '../../../interfaces/User'
import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'
import { useRouter } from 'next/router'
import useAtomWithRef from '../../../utils/useAtomWithRef'
import useAuth from '../../../hooks/auth'
import { useConnectModal } from '../../../hooks/walletModal'

const OuterDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-start;
  margin-top: 15px;
`

const ProfilePic = styled.div`
  min-width: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #323232;

  @media (min-width: 43em) {
    font-size: 17px;
    min-width: 50px;
    width: 50px;
    height: 50px;
  }
`

const CommentDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 15px;
`

const ButtonsDiv = styled.div`
  display: flex;
  flex-direction: row;
`

const CancelButton = styled(animated.div)`
  color: white;
  padding: 5px 15px;
  font-weight: 400;
  margin-top: 15px;
  font-size: 14px;
  cursor: pointer;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const CommentButton = styled(animated.div)`
  margin-left: 15px;
  background: white;
  color: black;
  border-radius: 10px;
  padding: 5px 15px;
  font-weight: 600;
  margin-top: 15px;
  font-size: 14px;
  cursor: pointer;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const fieldStyle = {
  root: {
    '& label.Mui-focused': {
      color: 'white',
      fontFamily: 'Inter',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'white',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'white',
      },
    },
  },
}

const StyledTextField = withStyles(fieldStyle)(TextField)

const theme = createMuiTheme({
  palette: {
    type: 'dark',
  },
})

interface Props {
  video: VideoData
  id?: string
  cancel?: () => void
  submit: (comment: string, comment_id?: string) => void
  typing: (isTyping: boolean) => void
}

const NewComment = ({ id, submit, cancel, typing }: Props): JSX.Element => {
  const [, , setSpaceBarActive] = useAtomWithRef(spacebarListenerActiveAtom)
  const [value, setValue] = React.useState('')
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [cancelHover, setCancelHover] = React.useState(false)
  const [submitHover, setSubmitHover] = React.useState(false)
  const [user, setUser] = React.useState<FbEnrichedUser | undefined>(undefined)
  const { profile } = useAuth()
  const setVisible = useConnectModal()
  const router = useRouter()

  const cancelHoverState = useSpring({
    color: cancelHover ? 'white' : '#cdcdcd',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const submitHoverState = useSpring({
    boxShadow: submitHover ? '0px 5px 25px rgba(255,255,255,0.2)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: submitHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const handleSubmit = React.useCallback(() => {
    submit(value, id)
    setValue('')
  }, [id, submit, value])

  const getUser = React.useCallback(async (address: string) => {
    const users = await fbEnrichUsersFromIds([address])
    if (users.length > 0) {
      setUser(users[0])
    }
  }, [])

  React.useEffect(() => {
    if (profile) {
      getUser(profile.address)
    } else {
      setUser(undefined)
    }
  }, [getUser, profile])

  React.useEffect(() => {
    typing(true)
  }, [value])

  const guardedSubmit = useFirebaseAuthGuard(handleSubmit)

  const openProfile = (): void => {
    if (profile) {
      router.push(`/${profile.address}`)
    }
  }

  return (
    <OuterDiv>
      <ProfilePic onClick={openProfile}>
        <ProfileImage item={user} size={isMobile ? 40 : 50} />
      </ProfilePic>
      <CommentDiv>
        <ThemeProvider theme={theme}>
          <StyledTextField
            fullWidth
            placeholder={id ? 'Add a reply' : 'Add a comment'}
            value={value}
            onChange={(event) => {
              setValue(event.target.value)
            }}
            onFocus={() => (profile ? setSpaceBarActive(false) : setVisible(true))}
            onBlur={() => setSpaceBarActive(true)}
            multiline
            variant="standard"
            InputProps={{ style: { fontSize: isMobile ? 14 : 17 } }}
            InputLabelProps={{ style: { fontSize: isMobile ? 14 : 17 } }}
          />
        </ThemeProvider>
        {value && value !== '' && (
          <ButtonsDiv>
            <CancelButton
              onMouseEnter={() => setCancelHover(true)}
              onMouseLeave={() => setCancelHover(false)}
              style={cancelHoverState}
              onClick={() => {
                setValue('')
                if (cancel) {
                  cancel()
                }
              }}
            >
              Cancel
            </CancelButton>
            <CommentButton
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
              style={submitHoverState}
              onClick={guardedSubmit}
            >
              {id ? 'Reply' : 'Comment'}
            </CommentButton>
          </ButtonsDiv>
        )}
      </CommentDiv>
    </OuterDiv>
  )
}

export default NewComment
